import { gsap } from 'gsap';
import EventBus from 'eventing-bus';
import { connect } from 'react-redux';
import { SplitText } from 'gsap/SplitText';
import PhoneInput from 'react-phone-input-2';
import { MsalProvider } from '@azure/msal-react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link, useParams } from 'react-router-dom';
import React, { useEffect, useState, useRef } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { PublicClientApplication } from '@azure/msal-browser';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';

import './index.css';
import { onSignUp } from '../../store/Auth';
import { getBranding } from '../../store/Settings';
import logo from '../../static/img/logo.svg';
import { oauthConfig } from '../../store/config';
import SocialLogin from '../../components/SocialLogin';

gsap.registerPlugin(SplitText, ScrollTrigger);
// Initialize MSAL instance for Microsoft authentication
const msalInstance = new PublicClientApplication({
  auth: {
    clientId: oauthConfig['microsoft']['clientId'],
    authority: oauthConfig['microsoft']['authority'],
    redirectUri: oauthConfig['microsoft']['redirectUri'],
  }, cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: false }
})

function Signup({ history, onSignUp, isLogin, auth, dispatch, currentBranding }) {
  const { hash } = useParams();
  const headingRefs = useRef([]);
  const paragraphRefs = useRef([]);
  const splitParagraphs = useRef([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({ step: 1, email: '', password: '', confirmPassword: '', hash: '', lastName: '', firstName: '', phone: '', privacyPolicy: false });

  const platformName = (currentBranding?.isCustom && currentBranding?.agencyName) ? currentBranding.agencyName : 'Sales AI';

  useEffect(() => { document.title = `Sign Up | ${platformName}` }, [platformName]);
  useEffect(() => { if (auth) history.push('/agents') }, [auth]);
  useEffect(() => { if (hash) setFormData(prevData => ({ ...prevData, step: 2, hash })); }, [hash]);

  const handleSingup = () => {
    // Validate additional fields if hash is present
    if (formData['step'] === 2) {
      if (!formData['lastName']) return EventBus.publish('error', 'Last name is required');
      if (!formData['phone']) return EventBus.publish('error', 'Phone number is required');
      if (!formData['firstName']) return EventBus.publish('error', 'First name is required');
      if (!formData['password']) return EventBus.publish('error', 'Password is required');
      if (!formData['confirmPassword']) return EventBus.publish('error', 'Confirm password is required');
      if (formData['password'] !== formData['confirmPassword']) return EventBus.publish('error', 'Passwords do not match');
      if (!formData['privacyPolicy']) return EventBus.publish('error', 'You must accept the Privacy Policy to continue');
    }

    onSignUp({ data: formData, history });
  }

  useEffect(() => {
    // Reset any previous SplitText instances
    splitParagraphs.current.forEach((split) => split?.revert?.());
    splitParagraphs.current = [];

    // Animate each heading
    headingRefs.current.forEach((el, i) => {
      gsap.fromTo(el, { opacity: 0, filter: "blur(10px)", y: -20 }, { opacity: 1, filter: "blur(0px)", y: 0, duration: 0.8, ease: "power3.out", delay: i * 0.3 });
    });

    // Animate each paragraph with SplitText
    paragraphRefs.current.forEach((el, i) => {
      const split = new SplitText(el, { type: "chars" });
      splitParagraphs.current.push(split);

      gsap.fromTo(
        split.chars,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          stagger: 0.02,
          ease: "power2.out",
          delay: (headingRefs.current.length + i) * 0.1,
        }
      );
    });
  }, []);

  return (
    <div className='sa-auth-page'>
      {/* ── Left Hero Background ── */}
      <div className='sa-hero-bg'>
        <div className='sa-mesh-overlay'></div>
        <div className='sa-grid-lines'></div>
        <div className='sa-particle sa-particle-1'></div>
        <div className='sa-particle sa-particle-2'></div>
        <div className='sa-particle sa-particle-3'></div>
        <div className='sa-particle sa-particle-4'></div>
        <div className='sa-particle sa-particle-5'></div>
        <div className='sa-particle sa-particle-6'></div>
        <div className='sa-particle sa-particle-7'></div>
        <div className='sa-particle sa-particle-8'></div>
        <div className='sa-shape sa-shape-ring'></div>
        <div className='sa-shape sa-shape-hex'></div>
        <div className='sa-shape sa-shape-tri'></div>
      </div>

      <div className='sa-layout'>
        {/* ── Left Content ── */}
        <div className='sa-left-content'>
          <div className='sa-brand'>
            <div className='sa-brand-logo'>
              {currentBranding?.['isCustom'] && currentBranding?.['logo'] ? (
                <img src={currentBranding['logo']} alt={currentBranding['agencyName'] || 'Logo'} style={{ height: '42px', maxWidth: '160px', objectFit: 'contain' }} />
              ) : (
                <svg width="42" height="42" viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="suO1" x1="0" y1="0.5" x2="1" y2="0.5"><stop offset="0%" stopColor="#e8709a" stopOpacity="0"/><stop offset="40%" stopColor="#ff9cc0"/><stop offset="60%" stopColor="#ff9cc0"/><stop offset="100%" stopColor="#e8709a" stopOpacity="0"/></linearGradient>
                    <linearGradient id="suO2" x1="0" y1="0.5" x2="1" y2="0.5"><stop offset="0%" stopColor="#c44070" stopOpacity="0"/><stop offset="40%" stopColor="#e8709a"/><stop offset="60%" stopColor="#e8709a"/><stop offset="100%" stopColor="#c44070" stopOpacity="0"/></linearGradient>
                    <radialGradient id="suN" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="#ffffff"/><stop offset="50%" stopColor="#ffb8d0"/><stop offset="100%" stopColor="#d4547a"/></radialGradient>
                    <filter id="suG" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur in="SourceGraphic" stdDeviation="2.2" result="blur"/><feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
                  </defs>
                  <ellipse cx="27" cy="27" rx="19" ry="7.5" transform="rotate(-40 27 27)" fill="none" stroke="url(#suO1)" strokeWidth="1.8"/>
                  <ellipse cx="27" cy="27" rx="19" ry="7.5" transform="rotate(40 27 27)" fill="none" stroke="url(#suO2)" strokeWidth="1.8"/>
                  <ellipse cx="27" cy="27" rx="19" ry="7.5" transform="rotate(90 27 27)" fill="none" stroke="url(#suO1)" strokeWidth="1.2" opacity="0.7"/>
                  <circle cx="8.5" cy="27" r="3" fill="#ff9cc0" filter="url(#suG)"/>
                  <circle cx="44.5" cy="22" r="2.4" fill="#e8709a" filter="url(#suG)"/>
                  <circle cx="30" cy="8.5" r="2.4" fill="#c44070" filter="url(#suG)"/>
                  <circle cx="27" cy="27" r="6.5" fill="rgba(84,21,48,0.5)" filter="url(#suG)"/>
                  <circle cx="27" cy="27" r="5" fill="url(#suN)"/>
                  <circle cx="27" cy="27" r="2.5" fill="white" opacity="0.95"/>
                </svg>
              )}
            </div>
            <span className='sa-brand-name'>{platformName}</span>
          </div>
          <div className='sa-hero-badge'>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            Join 12,000+ Sales Pros
          </div>
          <h1 className='sa-hero-headline'>Start your<br />journey to<br />smarter selling.</h1>
          <p className='sa-hero-subtitle'>Welcome to {platformName}—automated follow-ups, smart contact scoring, integrated messaging, and real-time analytics in one CRM.</p>

          <div className='sa-stats-grid'>
            <div className='sa-stat-card'>
              <span className='sa-stat-number'>3.2x</span>
              <span className='sa-stat-label'>Conversion</span>
            </div>
            <div className='sa-stat-card'>
              <span className='sa-stat-number'>12k+</span>
              <span className='sa-stat-label'>Active Sellers</span>
            </div>
            <div className='sa-stat-card'>
              <span className='sa-stat-number'>89%</span>
              <span className='sa-stat-label'>Time Saved</span>
            </div>
          </div>

          <div className='sa-testimonial'>
            <p className='sa-testimonial-text'>"{platformName} transformed our pipeline. We close 3x more deals with half the manual work. The AI follow-ups are a game-changer."</p>
            <div className='sa-testimonial-author'>
              <div className='sa-testimonial-avatar'>SK</div>
              <div>
                <strong>Sarah Kim</strong>
                <span>VP Sales, TechCorp</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right Content ── */}
        <div className='sa-right-content'>
          {formData['step'] === 1 && (
            <div className='sa-auth-card'>
              <div className='sa-card-icon'>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--sa-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
              </div>
              <h2 className='sa-card-title'>Create your Account</h2>
              <p className='sa-card-subtitle'>Enter your email to get started — it's quick and easy!</p>

              <ValidatorForm className='sa-form' onSubmit={handleSingup}>
                <div className='sa-field'>
                  <label className='sa-label'>Email</label>
                  <TextValidator
                    type='text'
                    name='email'
                    margin='dense'
                    variant='outlined'
                    placeholder='you@company.com'
                    value={formData['email']}
                    className='sa-input'
                    validators={['required', 'isEmail']}
                    errorMessages={['Email can not be empty', 'Email is not valid']}
                    onChange={(e) => setFormData({ ...formData, email: e['target']['value'] })}
                  />
                </div>
                <button type='submit' className='sa-btn-primary' disabled={isLogin}>
                  {!isLogin ? (
                    <>
                      Verify Email
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                    </>
                  ) : (
                    <i className='fa fa-spinner fa-spin'></i>
                  )}
                </button>
              </ValidatorForm>

              <>
                <div className='sa-divider'>
                  <span>OR SIGN UP WITH</span>
                </div>
                <div className='sa-social-login-wrapper'>
                  <GoogleOAuthProvider clientId={oauthConfig['google']['clientId']}>
                    <MsalProvider instance={msalInstance}>
                      <SocialLogin history={history} type='Sign up' />
                    </MsalProvider>
                  </GoogleOAuthProvider>
                </div>
              </>

              <p className='sa-switch-text'>
                Have an account? <Link to='/login'>Login</Link>
              </p>

              <div className='sa-trusted'>
                <span className='sa-trusted-label'>Trusted by teams at</span>
                <div className='sa-trusted-logos'>
                  <span className='sa-trusted-logo'>Stripe</span>
                  <span className='sa-trusted-logo'>Notion</span>
                  <span className='sa-trusted-logo'>Linear</span>
                  <span className='sa-trusted-logo'>Vercel</span>
                </div>
              </div>
            </div>
          )}

          {formData['step'] === 2 && (
            <div className='sa-auth-card sa-card-wide'>
              <div className='sa-card-icon'>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--sa-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
              </div>
              <h2 className='sa-card-title'>Create your Account</h2>
              <p className='sa-card-subtitle'>Complete your profile — it only takes a moment!</p>

              <ValidatorForm className='sa-form' onSubmit={handleSingup}>
                <div className='sa-field-row'>
                  <div className='sa-field'>
                    <label className='sa-label'>First Name</label>
                    <TextValidator
                      type='text'
                      name='firstName'
                      margin='dense'
                      variant='outlined'
                      validators={['required']}
                      placeholder='First name'
                      value={formData['firstName']}
                      className='sa-input'
                      errorMessages={['First name cannot be empty']}
                      onChange={(e) => setFormData({ ...formData, firstName: e['target']['value'] })}
                    />
                  </div>
                  <div className='sa-field'>
                    <label className='sa-label'>Last Name</label>
                    <TextValidator
                      type='text'
                      name='lastName'
                      margin='dense'
                      variant='outlined'
                      validators={['required']}
                      placeholder='Last name'
                      value={formData['lastName']}
                      className='sa-input'
                      errorMessages={['Last name cannot be empty']}
                      onChange={(e) => setFormData({ ...formData, lastName: e['target']['value'] })}
                    />
                  </div>
                </div>

                <div className='sa-field sa-phone-field'>
                  <label className='sa-label'>Phone Number</label>
                  <PhoneInput
                    country={'us'}
                    value={formData['phone']}
                    placeholder='Phone number'
                    containerStyle={{ width: '100%' }}
                    inputStyle={{
                      width: '100%',
                      height: '48px',
                      fontSize: '15px',
                      borderRadius: '10px',
                      border: '1.5px solid var(--sa-border)',
                      background: 'var(--sa-input-bg)',
                      fontFamily: 'var(--sa-font-body)',
                      color: 'var(--sa-text-dark)',
                      paddingLeft: '48px'
                    }}
                    buttonStyle={{
                      borderRadius: '10px 0 0 10px',
                      border: '1.5px solid var(--sa-border)',
                      background: 'var(--sa-input-bg)'
                    }}
                    onChange={(value) => setFormData({ ...formData, phone: `+${value}` })}
                  />
                </div>

                <div className='sa-field-row'>
                  <div className='sa-field sa-field-password'>
                    <label className='sa-label'>Password</label>
                    <TextValidator
                      margin='dense'
                      name='password'
                      variant='outlined'
                      validators={['required']}
                      placeholder='Password'
                      value={formData['password']}
                      className='sa-input'
                      type={showPassword ? 'text' : 'password'}
                      errorMessages={['Password cannot be empty']}
                      onChange={(e) => setFormData({ ...formData, password: e['target']['value'] })}
                    />
                    <button type='button' className='sa-toggle-password' onClick={() => setShowPassword(!showPassword)}>
                      <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                  <div className='sa-field sa-field-password'>
                    <label className='sa-label'>Confirm Password</label>
                    <TextValidator
                      margin='dense'
                      variant='outlined'
                      name='confirmPassword'
                      validators={['required']}
                      placeholder='Confirm password'
                      className='sa-input'
                      value={formData['confirmPassword']}
                      type={showConfirmPassword ? 'text' : 'password'}
                      errorMessages={['Confirm password cannot be empty']}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e['target']['value'] })}
                    />
                    <button type='button' className='sa-toggle-password' onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                      <i className={`fa ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </div>

                <div className='sa-privacy'>
                  <input
                    type='checkbox'
                    id='privacyPolicy'
                    checked={formData['privacyPolicy']}
                    onChange={(e) => setFormData({ ...formData, privacyPolicy: e['target']['checked'] })}
                  />
                  <label htmlFor='privacyPolicy'>I accept the <Link to='/privacy-policy'>Privacy Policy</Link></label>
                </div>

                <button type='submit' className='sa-btn-primary' disabled={isLogin}>
                  {!isLogin ? (
                    <>
                      Sign Up
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                    </>
                  ) : (
                    <i className='fa fa-spinner fa-spin'></i>
                  )}
                </button>
              </ValidatorForm>

              <p className='sa-switch-text'>
                <Link to='/signup'>Back</Link> &nbsp;|&nbsp; Have an account? <Link to='/login'>Login</Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>

    /* ── OLD UI (commented out) ──
    <div className='login-page signup-page'>
      <div className='logo-area'>
        <></>
      </div>
      <div className='auto-container'>
        <div className='row'>
          <div className='col-lg-6 col-md-6 col-sm-12 login-area'>
            <div className='left-img-area'>
              <img src={require('../../static/img/signup.png')} alt='' />
              <div className="title-bottom">
                <h2>Sales AI</h2>
                <p>Welcome to Sales AI—automated follow-ups, smart contact scoring, integrated messaging, and real-time analytics in one CRM.</p>
              </div>
            </div>
          </div>
          ... (step 1 and step 2 forms) ...
        </div>
      </div>
    </div>
    */
  )
}

const mapDispatchToProps = dispatch => ({
  onSignUp: data => dispatch(onSignUp(data)),
  dispatch
});
const mapStateToProps = ({ Auth: { isLogin, auth }, Settings: { currentBranding } }) => ({ isLogin, auth, currentBranding });

export default connect(mapStateToProps, mapDispatchToProps)(Signup);